import React from 'react';
import * as FeatherIcons from 'react-feather';
import PropTypes from 'prop-types';

/**
 * Badge Component
 * Displays a trust badge or feature badge with icon and label
 *
 * @param {string} icon - Feather icon name (e.g., 'Award', 'Shield')
 * @param {string} label - Badge label text
 * @param {string} description - Optional description text
 * @param {string} variant - Badge style variant: 'trust', 'feature', 'stat'
 * @param {string} size - Badge size: 'sm', 'md', 'lg'
 */
const Badge = ({ icon, label, description, variant = 'trust', size = 'md' }) => {
    // Get the icon component from react-feather
    const IconComponent = FeatherIcons[icon] || FeatherIcons.Check;

    const sizeMap = {
        sm: { icon: 16, fontSize: '12px' },
        md: { icon: 20, fontSize: '14px' },
        lg: { icon: 24, fontSize: '16px' },
    };

    const variantStyles = {
        trust: {
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            iconColor: '#0066CC',
            textColor: '#2C3E50',
        },
        feature: {
            backgroundColor: 'transparent',
            border: 'none',
            iconColor: '#00A86B',
            textColor: '#2C3E50',
        },
        stat: {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            iconColor: '#FFFFFF',
            textColor: '#FFFFFF',
        },
    };

    const currentSize = sizeMap[size] || sizeMap.md;
    const currentVariant = variantStyles[variant] || variantStyles.trust;

    const badgeStyle = {
        display: 'flex',
        alignItems: description ? 'flex-start' : 'center',
        gap: '12px',
        padding: description ? '16px' : '12px',
        backgroundColor: currentVariant.backgroundColor,
        border: currentVariant.border,
        borderRadius: '8px',
        transition: 'all 0.2s ease-in-out',
    };

    const iconWrapperStyle = {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: description ? '2px' : '0',
    };

    const contentStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    };

    const labelStyle = {
        fontSize: currentSize.fontSize,
        fontWeight: 600,
        color: currentVariant.textColor,
        lineHeight: 1.4,
    };

    const descriptionStyle = {
        fontSize: '13px',
        fontWeight: 400,
        color: variant === 'stat' ? 'rgba(255, 255, 255, 0.8)' : '#546E7A',
        lineHeight: 1.4,
    };

    return (
        <div className={`badge badge-${variant} badge-${size}`} style={badgeStyle}>
            <div style={iconWrapperStyle}>
                <IconComponent
                    size={currentSize.icon}
                    color={currentVariant.iconColor}
                    strokeWidth={2}
                />
            </div>
            <div style={contentStyle}>
                <div style={labelStyle}>{label}</div>
                {description && <div style={descriptionStyle}>{description}</div>}
            </div>
        </div>
    );
};

Badge.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    variant: PropTypes.oneOf(['trust', 'feature', 'stat']),
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default Badge;
